import React, { useState, useEffect, useRef } from 'react';
import { useAppStore } from '../stores/useAppStore';
import { coinMetricsService } from '../services/coinMetricsService';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import { useToastStore } from '../stores/useToastStore';
import { indicatorDescriptions } from '../constants/indicatorDescriptions';
import KlineChart, { KlineChartRef } from '../components/KlineChart';
import { KlineCandle } from '../services/klineTypes';
import { BarInterval, PositionSide, POSITION_SIDE } from '@/services/enums';
import PageHeader from '../components/PageHeader';
import { COMMON_TIMEFRAMES } from '../constants/timeframes';

const SIGNAL_LABEL_MAP: Record<string, string> = {
  BULLISH: '看涨',
  BEARISH: '看跌',
  NEUTRAL: '中性',
};

const SIGNAL_COLOR_MAP: Record<string, { bg: string; text: string; border: string }> = {
  BULLISH: { bg: 'bg-green-500/10', text: 'text-green-300', border: 'border-green-500/30' },
  BEARISH: { bg: 'bg-red-500/10', text: 'text-red-300', border: 'border-red-500/30' },
  NEUTRAL: { bg: 'bg-yellow-500/10', text: 'text-yellow-300', border: 'border-yellow-500/30' },
};

const CoinMetricsAnalysis: React.FC = () => {
  const { playbackSymbol, setCurrentPage } = useAppStore();
  const showToast = useToastStore((state) => state.show);

  // 返回上一个页面
  const handleBack = () => {
    const previousPage = sessionStorage.getItem('coin_metrics_previous_page') || 'watchlist-self';
    setCurrentPage(previousPage as any);
  };
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);
  const [aiWebSearchLoading, setAiWebSearchLoading] = useState(false);
  const [aiWebSearchResult, setAiWebSearchResult] = useState<any>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [orderAnalysis, setOrderAnalysis] = useState<any>(null);
  const [orderAnalysisLoading, setOrderAnalysisLoading] = useState(false);
  const [recommendedStrategy, setRecommendedStrategy] = useState<string | null>(null); // 保存操作建议
  const [activeTab, setActiveTab] = useState<PositionSide>(POSITION_SIDE.LONG as PositionSide);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [positionSize, setPositionSize] = useState<number>(2000); // 仓位大小（USDT），默认2000
  const [longOrderSubmitted, setLongOrderSubmitted] = useState(false); // 做多订单已提交
  const [shortOrderSubmitted, setShortOrderSubmitted] = useState(false); // 做空订单已提交
  const [selectedTimeframe, setSelectedTimeframe] = useState<BarInterval>('1H'); // 时间粒度
  const hasLoadedRef = useRef<string | null>(null); // 记录已加载的key，防止React StrictMode重复调用
  const chartRef = useRef<KlineChartRef>(null); // K线图引用

  useEffect(() => {
    if (!playbackSymbol) {
      setError('未选择币种');
      setLoading(false);
      return;
    }

    // 生成唯一的加载 key
    const loadKey = `${playbackSymbol}-${selectedTimeframe}`;

    // 防止React StrictMode导致的重复调用
    if (hasLoadedRef.current === loadKey) {
      // 如果已经加载过，确保loading状态为false
      setLoading(false);
      return;
    }

    hasLoadedRef.current = loadKey;

    const loadMetrics = async () => {
      try {
        setLoading(true);
        setError(null);

        // 先尝试获取已有数据
        const existingMetrics = await coinMetricsService.getCoinMetrics(playbackSymbol, selectedTimeframe);

        if (existingMetrics) {
          setMetrics(existingMetrics);
          setLoading(false);
          // 默认展开"价格数据"分类，其他折叠
          setExpandedCategories(new Set(['价格数据']));
        } else {
          // 如果没有数据，不自动计算，只设置加载完成
          setMetrics(null);
          setLoading(false);
        }
      } catch (err: any) {
        console.error('加载指标失败:', err);
        setError(err.message);
        setMetrics(null); // 确保清空metrics，以便错误页面能正确显示
        setLoading(false);
      } finally {
        // 这里不设置loading，因为已经在try/catch中设置了
      }
    };

    loadMetrics();

    // 清理函数：组件卸载或依赖变化时，清除loadKey
    return () => {
      // 注意：不在这里清除hasLoadedRef，因为React StrictMode会在同一渲染周期内执行两次
      // 只在新的key加载时清除旧的key
      if (hasLoadedRef.current === loadKey) {
        // 延迟清除，避免StrictMode的第二次调用
        setTimeout(() => {
          if (hasLoadedRef.current === loadKey) {
            hasLoadedRef.current = null;
          }
        }, 100);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbackSymbol, selectedTimeframe]);

  // 根据粒度计算应该拉取的时间范围（小时数）
  const getHoursByInterval = (interval: string): number => {
    switch (interval) {
      case '5m':
        return 24; // 24小时
      case '15m':
        return 24 * 3; // 72小时（3天）
      case '30m':
        return 24 * 6; // 144小时（6天）
      case '1H':
        return 24 * 10; // 240小时（10天）
      case '4H':
        return 24 * 30; // 720小时（30天）
      case '1D':
        return 24 * 90; // 2160小时（90天）
      default:
        return 24; // 默认24小时
    }
  };

  // K线数据加载完成回调
  const handleKlineLoad = (candles: KlineCandle[]) => {
    console.log(`[CoinMetrics] K线数据加载完成: ${candles.length} 条`);
  };

  // K线数据加载错误回调
  const handleKlineError = (errorMsg: string) => {
    console.error('[CoinMetrics] 加载K线数据失败:', errorMsg);
  };

  const calculateAndLoad = async () => {
    if (!playbackSymbol) return;

    try {
      setCalculating(true);
      setError(null);
      // 不清空之前的数据，保持显示原有指标

      // K线数据由KlineChart组件自动加载，无需手动加载
      // 执行指标计算
      const calculatedMetrics = await coinMetricsService.calculateCoinMetrics(playbackSymbol, selectedTimeframe);

      setMetrics(calculatedMetrics);
      // 默认展开"价格数据"分类，其他折叠
      setExpandedCategories(new Set(['价格数据']));
      showToast('指标计算完成', 'success');
    } catch (err: any) {
      console.error('计算指标失败:', err);
      setError(err.message || '计算指标失败');
      showToast(err.message || '计算指标失败', 'error');
    } finally {
      setCalculating(false);
      setLoading(false);
    }
  };

  const formatValue = (key: string, value: any): string => {
    if (value === null || value === undefined) return '-';

    // 如果是字符串类型，尝试转换为数字
    let num: number;
    if (typeof value === 'string') {
      num = parseFloat(value);
      if (isNaN(num)) return value; // 无法转换为数字，直接返回原字符串
    } else if (typeof value === 'number') {
      num = value;
    } else if (typeof value === 'boolean') {
      return value ? '是' : '否';
    } else {
      // 其他类型（对象、数组等），直接转换为字符串
      return String(value);
    }

    // 确保 num 是有效的数字
    if (isNaN(num) || !isFinite(num)) {
      return String(value);
    }

    // 百分比字段（changePct*, spreadPct, *Pct等）
    if (
      key.includes('Pct') ||
      key.includes('pct') ||
      key.includes('Ratio') ||
      key.includes('ratio') ||
      key === 'turnover24h'
    ) {
      return num.toFixed(2) + '%';
    }

    // 大数字格式化（价格、成交量等）
    if (Math.abs(num) >= 1e12) return (num / 1e12).toFixed(2) + 'T';
    if (Math.abs(num) >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (Math.abs(num) >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (Math.abs(num) >= 1e3) return (num / 1e3).toFixed(2) + 'K';

    // 小数字（涨跌额等）
    if (Math.abs(num) < 0.01 && num !== 0) {
      return num.toFixed(8);
    }

    return num.toFixed(4);
  };

  const getIndicatorValue = (key: string): any => {
    if (!metrics) return null;
    return metrics[key];
  };

  // 将字段名转换为友好的显示名称
  const formatFieldName = (key: string): string => {
    // 如果已经在 indicatorDescriptions 中定义，使用定义的名称
    if (indicatorDescriptions[key]) {
      return indicatorDescriptions[key].name;
    }

    // 将 camelCase 转换为空格分隔的格式
    const formatted = key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();

    // 处理一些常见的缩写和特殊格式
    return formatted
      .replace(/Sma/g, 'SMA')
      .replace(/Ema/g, 'EMA')
      .replace(/Wma/g, 'WMA')
      .replace(/Rsi/g, 'RSI')
      .replace(/Macd/g, 'MACD')
      .replace(/Atr/g, 'ATR')
      .replace(/Cci/g, 'CCI')
      .replace(/Mfi/g, 'MFI')
      .replace(/Roc/g, 'ROC')
      .replace(/Obv/g, 'OBV')
      .replace(/Vwap/g, 'VWAP')
      .replace(/Cmf/g, 'CMF')
      .replace(/Adx/g, 'ADX')
      .replace(/Pct/g, '%')
      .replace(/Usd/g, 'USD')
      .replace(/24h/g, '24H')
      .replace(/7d/g, '7D')
      .replace(/30d/g, '30D')
      .replace(/90d/g, '90D')
      .replace(/1y/g, '1Y');
  };

  const getIndicatorInfo = (key: string) => {
    // 直接从描述定义中获取
    if (indicatorDescriptions[key]) {
      return indicatorDescriptions[key];
    }

    // 如果没有定义，返回基本默认值
    const category = getDefaultCategory(key);
    return {
      name: formatFieldName(key),
      description: formatFieldName(key),
      category: category,
    };
  };

  // 获取字段的默认分类（基于字段名）
  const getDefaultCategory = (key: string): string => {
    const lowerKey = key.toLowerCase();
    if (
      lowerKey.includes('price') ||
      lowerKey.includes('open') ||
      lowerKey.includes('high') ||
      lowerKey.includes('low')
    ) {
      return '价格数据';
    }
    if (lowerKey.includes('change') || lowerKey.includes('pct')) {
      return '价格变化率';
    }
    if (lowerKey.includes('volume') || lowerKey.includes('turnover')) {
      return '交易量数据';
    }
    if (lowerKey.includes('bid') || lowerKey.includes('ask') || lowerKey.includes('spread')) {
      return '买卖盘数据';
    }
    if (lowerKey.includes('sma') || lowerKey.includes('ema') || lowerKey.includes('wma')) {
      return '趋势指标';
    }
    if (
      lowerKey.includes('rsi') ||
      lowerKey.includes('macd') ||
      lowerKey.includes('stoch') ||
      lowerKey.includes('cci') ||
      lowerKey.includes('williams') ||
      lowerKey.includes('mfi') ||
      lowerKey.includes('roc')
    ) {
      return '动量指标';
    }
    if (
      lowerKey.includes('bollinger') ||
      lowerKey.includes('bb') ||
      lowerKey.includes('atr') ||
      lowerKey.includes('volatility') ||
      lowerKey.includes('keltner')
    ) {
      return '波动性指标';
    }
    if (
      lowerKey.includes('obv') ||
      lowerKey.includes('vwap') ||
      lowerKey.includes('cmf') ||
      lowerKey.includes('adline')
    ) {
      return '成交量指标';
    }
    if (lowerKey.includes('support') || lowerKey.includes('resistance') || lowerKey.includes('pivot')) {
      return '支撑阻力';
    }
    if (
      lowerKey.includes('adx') ||
      lowerKey.includes('di') ||
      lowerKey.includes('aroon') ||
      lowerKey.includes('trend') ||
      lowerKey.includes('momentum')
    ) {
      return '趋势强度';
    }
    if (lowerKey.includes('signal') || lowerKey.includes('cross')) {
      return '信号提示';
    }
    if (lowerKey.includes('funding') || lowerKey.includes('interest') || lowerKey.includes('ratio')) {
      return '衍生指标';
    }
    if (
      lowerKey.includes('market') ||
      lowerKey.includes('cap') ||
      lowerKey.includes('supply') ||
      lowerKey.includes('liquidity')
    ) {
      return '市场数据';
    }
    if (
      lowerKey.includes('risk') ||
      lowerKey.includes('score') ||
      lowerKey.includes('sharpe') ||
      lowerKey.includes('sortino') ||
      lowerKey.includes('drawdown')
    ) {
      return '风险评估';
    }
    if (lowerKey.includes('correlation') || lowerKey.includes('beta') || lowerKey.includes('exchange')) {
      return '关联数据';
    }
    if (
      lowerKey.includes('id') ||
      lowerKey.includes('symbol') ||
      lowerKey.includes('name') ||
      lowerKey.includes('type') ||
      lowerKey.includes('category') ||
      lowerKey.includes('state') ||
      lowerKey.includes('date') ||
      lowerKey.includes('created') ||
      lowerKey.includes('updated') ||
      lowerKey.includes('sync') ||
      lowerKey.includes('source') ||
      lowerKey.includes('quality')
    ) {
      return '元数据'; // 这些字段不显示
    }
    return '其他';
  };

  // 按分类组织指标 - 只显示在 indicatorDescriptions 中定义的字段
  const groupedIndicators: { [category: string]: string[] } = {};
  if (metrics) {
    Object.keys(metrics).forEach((key) => {
      // 只显示在 indicatorDescriptions 中定义的字段
      if (!indicatorDescriptions[key]) {
        return;
      }

      // 跳过元数据字段
      const category = getDefaultCategory(key);
      if (category === '元数据') {
        return;
      }

      // 跳过 null 或 undefined 值
      if (metrics[key] === null || metrics[key] === undefined) {
        return;
      }

      // 使用 indicatorDescriptions 中定义的分类
      const indicatorCategory = indicatorDescriptions[key].category;

      if (!groupedIndicators[indicatorCategory]) {
        groupedIndicators[indicatorCategory] = [];
      }
      groupedIndicators[indicatorCategory].push(key);
    });

    // 对每个分类的指标进行排序
    Object.keys(groupedIndicators).forEach((category) => {
      if (category === '价格数据') {
        // 价格数据分类：price 放在最前面，其他按字母顺序
        groupedIndicators[category].sort((a, b) => {
          if (a === 'price') return -1;
          if (b === 'price') return 1;
          return a.localeCompare(b);
        });
      } else {
        // 其他分类按字母顺序排序
        groupedIndicators[category].sort();
      }
    });
  }

  // 定义分类显示顺序（"其他"放在最后，但在AI分析之前）
  const categoryOrder = [
    '价格数据',
    '价格变化率',
    '交易量数据',
    '买卖盘数据',
    '趋势指标',
    '动量指标',
    '波动性指标',
    '成交量指标',
    '支撑阻力',
    '趋势强度',
    '信号提示',
    '市场数据',
    '关联数据',
    '衍生指标',
    '风险评估',
    '其他', // 放在最后
  ];

  // 按定义的顺序排序分类
  const sortedCategories = Object.keys(groupedIndicators).sort((a, b) => {
    const indexA = categoryOrder.indexOf(a);
    const indexB = categoryOrder.indexOf(b);

    // 如果两个分类都在顺序列表中，按顺序排序
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // 如果只有a在列表中，a排在前面
    if (indexA !== -1) return -1;
    // 如果只有b在列表中，b排在前面
    if (indexB !== -1) return 1;
    // 如果都不在列表中，按字母顺序排序，但"其他"放在最后
    if (a === '其他') return 1;
    if (b === '其他') return -1;
    return a.localeCompare(b);
  });

  const handleAIWebSearch = async () => {
    if (!playbackSymbol) return;
    try {
      setAiWebSearchLoading(true);
      setAiWebSearchResult(null); // 先清空之前的结果

      const result = await coinMetricsService.searchCoinNewsWithAI(playbackSymbol);

      console.log('AI 联网搜索结果:', result);

      setAiWebSearchResult(result);
      setAiWebSearchLoading(false);

      showToast('AI 联网搜索完成', 'success');
    } catch (err: any) {
      console.error('AI 联网搜索失败:', err);
      setAiWebSearchLoading(false);
      setAiWebSearchResult(null); // 出错时清空结果
      showToast(err.message || 'AI 联网搜索失败', 'error');
    }
  };

  // 渲染 AI 联网搜索 - 市场情绪
  const renderMarketSentimentBlock = (marketSentiment: any) => {
    if (!marketSentiment) return null;

    if (typeof marketSentiment === 'string') {
      return <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-white/70">{marketSentiment}</div>;
    }

    if (typeof marketSentiment === 'object') {
      const { audieraSentiment, abnormalStatus, comparison, evidence, ...rest } = marketSentiment;
      return (
        <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-white/70 space-y-1">
          {audieraSentiment && <div>情绪：{audieraSentiment}</div>}
          {typeof abnormalStatus !== 'undefined' && <div>异常状态：{abnormalStatus ? '是' : '否'}</div>}
          {comparison && <div>对比：{comparison}</div>}
          {evidence && <div>佐证：{evidence}</div>}
          {Object.keys(rest || {}).length > 0 && (
            <div className="text-xs text-white/40">其他：{JSON.stringify(rest)}</div>
          )}
        </div>
      );
    }

    return (
      <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-white/70">{String(marketSentiment)}</div>
    );
  };

  // 渲染 AI 联网搜索 - 风险评估
  const renderRiskAssessmentBlock = (riskAssessment: any) => {
    if (!riskAssessment) return null;

    if (typeof riskAssessment === 'string') {
      return (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-yellow-200 leading-relaxed">
          {riskAssessment}
        </div>
      );
    }

    if (typeof riskAssessment === 'object') {
      const { overallRisk, majorRisks, monitoringFocus, ...rest } = riskAssessment;
      return (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-yellow-200 leading-relaxed space-y-2">
          {overallRisk && <div>总体风险：{overallRisk}</div>}
          {Array.isArray(majorRisks) && majorRisks.length > 0 && (
            <div>
              <div className="font-semibold text-yellow-100">主要风险</div>
              <ul className="list-disc list-inside space-y-1">
                {majorRisks.map((r: any, idx: number) => (
                  <li key={idx}>{typeof r === 'string' ? r : JSON.stringify(r)}</li>
                ))}
              </ul>
            </div>
          )}
          {Array.isArray(monitoringFocus) && monitoringFocus.length > 0 && (
            <div>
              <div className="font-semibold text-yellow-100">监控重点</div>
              <ul className="list-disc list-inside space-y-1">
                {monitoringFocus.map((m: any, idx: number) => (
                  <li key={idx}>{typeof m === 'string' ? m : JSON.stringify(m)}</li>
                ))}
              </ul>
            </div>
          )}
          {Object.keys(rest || {}).length > 0 && (
            <div className="text-xs text-yellow-200/70">其他：{JSON.stringify(rest)}</div>
          )}
        </div>
      );
    }

    return (
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-yellow-200 leading-relaxed">
        {String(riskAssessment)}
      </div>
    );
  };

  // 渲染 AI 联网搜索 - 操作建议
  const renderTradingAdviceBlock = (tradingAdvice: any) => {
    if (!tradingAdvice) return null;

    if (typeof tradingAdvice === 'string') {
      return (
        <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-white/70 leading-relaxed">
          {tradingAdvice}
        </div>
      );
    }

    if (typeof tradingAdvice === 'object') {
      const { action, reason, riskControl, positionHint, ...rest } = tradingAdvice;
      return (
        <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-white/70 leading-relaxed space-y-2">
          {action && <div>建议：{action}</div>}
          {reason && <div>理由：{reason}</div>}
          {positionHint && <div>仓位提示：{positionHint}</div>}
          {riskControl && (
            <div className="text-sm text-white/70">
              风险控制：{typeof riskControl === 'string' ? riskControl : JSON.stringify(riskControl)}
            </div>
          )}
          {Object.keys(rest || {}).length > 0 && (
            <div className="text-xs text-white/50">其他：{JSON.stringify(rest)}</div>
          )}
        </div>
      );
    }

    return (
      <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-white/70 leading-relaxed">
        {String(tradingAdvice)}
      </div>
    );
  };

  const handleAIAnalyze = async () => {
    if (!playbackSymbol) return;
    try {
      setAiLoading(true);
      setAiAnalysis(null); // 先清空之前的结果

      const result = await coinMetricsService.analyzeCoinMetrics(playbackSymbol, selectedTimeframe);

      console.log('AI 分析结果:', result);

      // 先设置分析结果，再设置 loading 为 false
      // 这样可以确保状态更新顺序正确
      setAiAnalysis(result);
      setAiLoading(false);

      showToast('AI 分析完成', 'success');
    } catch (err: any) {
      console.error('AI 分析失败:', err);
      setAiLoading(false);
      setAiAnalysis(null); // 出错时清空结果
      showToast(err.message || 'AI 分析失败', 'error');
    }
  };

  if (loading) {
    return (
      <GradientBackground>
        <ParticleBackground />
        <div className="min-h-screen">
          {/* 头部导航栏 */}
          <PageHeader title={playbackSymbol || '指标分析'} onBack={handleBack} />

          {/* 时间粒度选择栏 */}
          <div className="fixed top-12 left-0 right-0 z-40 bg-black/20 backdrop-blur-md border-b border-white/10">
            <div className="flex items-center justify-between px-4 py-2 gap-4">
              <select
                value={selectedTimeframe}
                onChange={(e) => setSelectedTimeframe(e.target.value as BarInterval)}
                className="flex-1 px-3 py-1.5 bg-white/10 text-white border border-white/20 rounded-lg text-sm focus:outline-none focus:border-white/40"
              >
                {COMMON_TIMEFRAMES.map((opt) => (
                  <option key={opt.value} value={opt.value} className="bg-gray-800 text-white">
                    {opt.label}
                  </option>
                ))}
              </select>
              <button
                onClick={calculateAndLoad}
                disabled={calculating}
                className="px-3 py-1.5 bg-green-500/20 text-green-300 border border-green-500/50 rounded-lg hover:bg-green-500/30 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
              >
                {calculating ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>计算中...</span>
                  </>
                ) : (
                  '重新计算'
                )}
              </button>
            </div>
          </div>

          {/* 加载内容 */}
          <div className="pt-40 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
          </div>
        </div>
      </GradientBackground>
    );
  }

  if (error && !metrics) {
    return (
      <GradientBackground>
        <ParticleBackground />
        <div className="min-h-screen">
          {/* 头部导航栏 */}
          <PageHeader title={playbackSymbol || '指标分析'} onBack={handleBack} />

          {/* 时间粒度选择栏 */}
          <div className="fixed top-12 left-0 right-0 z-40 bg-black/20 backdrop-blur-md border-b border-white/10">
            <div className="flex items-center justify-between px-4 py-2 gap-4">
              <select
                value={selectedTimeframe}
                onChange={(e) => setSelectedTimeframe(e.target.value as BarInterval)}
                className="flex-1 px-3 py-1.5 bg-white/10 text-white border border-white/20 rounded-lg text-sm focus:outline-none focus:border-white/40"
              >
                {COMMON_TIMEFRAMES.map((opt) => (
                  <option key={opt.value} value={opt.value} className="bg-gray-800 text-white">
                    {opt.label}
                  </option>
                ))}
              </select>
              <button
                onClick={calculateAndLoad}
                disabled={calculating}
                className="px-3 py-1.5 bg-green-500/20 text-green-300 border border-green-500/50 rounded-lg hover:bg-green-500/30 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
              >
                {calculating ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>计算中...</span>
                  </>
                ) : (
                  '重新计算'
                )}
              </button>
            </div>
          </div>

          {/* 错误内容 */}
          <div className="pt-24 flex items-center justify-center min-h-screen p-4">
            <div className="text-center max-w-md">
              <div className="text-red-400 mb-4 text-lg">{error}</div>
            </div>
          </div>
        </div>
      </GradientBackground>
    );
  }

  return (
    <GradientBackground>
      <ParticleBackground />
      <div className="min-h-screen">
        {/* 头部 */}
        <PageHeader title={playbackSymbol || '指标分析'} onBack={handleBack} />

        {/* 时间粒度选择栏 */}
        <div className="fixed top-12 left-0 right-0 z-40 bg-black/20 backdrop-blur-md border-b border-white/10">
          <div className="flex items-center justify-between px-4 py-2 gap-4">
            <select
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value as BarInterval)}
              className="flex-1 px-3 py-1.5 bg-white/10 text-white border border-white/20 rounded-lg text-sm focus:outline-none focus:border-white/40"
            >
              {COMMON_TIMEFRAMES.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-gray-800 text-white">
                  {opt.label}
                </option>
              ))}
            </select>
            <button
              onClick={calculateAndLoad}
              disabled={calculating}
              className="px-3 py-1.5 bg-green-500/20 text-green-300 border border-green-500/50 rounded-lg hover:bg-green-500/30 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
            >
              {calculating ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>计算中...</span>
                </>
              ) : (
                '重新计算'
              )}
            </button>
          </div>
        </div>

        {/* 内容区域 */}
        <div className="pt-24 px-4 pb-24">
          {/* K线图区域 */}
          {playbackSymbol && (
            <div className="mt-4 mb-4 bg-black/10 backdrop-blur-sm border border-white/10 rounded-lg overflow-visible">
              <KlineChart
                ref={chartRef}
                symbol={playbackSymbol}
                bar={selectedTimeframe as BarInterval}
                theme="dark"
                showVolume={true}
                autoScroll={false}
                height={400}
                autoLoad={true}
                loadOptions={{
                  start: (() => {
                    const hours = getHoursByInterval(selectedTimeframe);
                    const endTime = Math.floor(Date.now() / 1000);
                    return endTime - hours * 60 * 60;
                  })(),
                  end: Math.floor(Date.now() / 1000),
                  limit: 20000,
                }}
                onLoad={handleKlineLoad}
                onError={handleKlineError}
              />
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
              <div className="text-red-300 text-sm">{error}</div>
            </div>
          )}

          {metrics && (
            <div className="space-y-4 mt-2">
              {sortedCategories.map((category) => {
                const isExpanded = expandedCategories.has(category);

                return (
                  <div key={category} className="bg-white/5 border border-white/20 rounded-xl overflow-hidden">
                    <button
                      onClick={() => {
                        setExpandedCategories((prev) => {
                          const newSet = new Set(prev);
                          if (newSet.has(category)) {
                            newSet.delete(category);
                          } else {
                            newSet.add(category);
                          }
                          return newSet;
                        });
                      }}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
                    >
                      <h2 className="text-lg font-bold text-white">{category}</h2>
                      <svg
                        className={`w-5 h-5 text-white/60 transition-transform duration-200 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-3 border-t border-white/10 pt-3">
                        {groupedIndicators[category].map((key) => {
                          const value = getIndicatorValue(key);
                          const info = getIndicatorInfo(key);

                          if (value === null || value === undefined) return null;

                          return (
                            <div
                              key={key}
                              className="bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/10 transition-colors"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="font-semibold text-white mb-1">{info.name}</div>
                                  <div className="text-sm text-white/60 mb-2">{info.description}</div>
                                  <div className="text-lg font-mono text-cyan-400">{formatValue(key, value)}</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {metrics?.technicalPrediction && (
            <div className="mt-6 space-y-4">
              <div className="bg-white/5 border border-white/20 rounded-xl p-4 space-y-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-white/60">量化指标预测</p>
                    <h2 className="text-xl font-bold text-white">技术指标概率模型</h2>
                    <p className="text-xs text-white/50">
                      综合 EMA/SMA、RSI、MACD、布林带、资金流、资金费率、支撑阻力等信号计算
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white/60">总体信号</span>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        SIGNAL_COLOR_MAP[metrics.technicalPrediction.overallSignal || 'NEUTRAL']?.bg || 'bg-white/10'
                      } ${
                        SIGNAL_COLOR_MAP[metrics.technicalPrediction.overallSignal || 'NEUTRAL']?.text || 'text-white'
                      }`}
                    >
                      {SIGNAL_LABEL_MAP[metrics.technicalPrediction.overallSignal] || '中性'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                    <div className="text-white/60 text-sm mb-1">上涨概率</div>
                    <div className="text-3xl font-bold text-green-300">
                      {metrics.technicalPrediction.upProbability?.toFixed(2)}%
                    </div>
                    <div className="mt-2 h-2 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-400 rounded-full"
                        style={{ width: `${metrics.technicalPrediction.upProbability}%` }}
                      />
                    </div>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                    <div className="text-white/60 text-sm mb-1">下跌概率</div>
                    <div className="text-3xl font-bold text-red-300">
                      {metrics.technicalPrediction.downProbability?.toFixed(2)}%
                    </div>
                    <div className="mt-2 h-2 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-red-400 rounded-full"
                        style={{ width: `${metrics.technicalPrediction.downProbability}%` }}
                      />
                    </div>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                    <div className="text-white/60 text-sm mb-1">综合得分</div>
                    <div className="text-3xl font-bold text-cyan-300">
                      {metrics.technicalPrediction.weightedScore?.toFixed(3)}
                    </div>
                    <div className="text-xs text-white/40 mt-1">&gt; 0 看涨， &lt; 0 看跌</div>
                  </div>
                </div>

                {metrics.technicalPrediction.signalDetails?.length > 0 && (
                  <div>
                    <div className="text-sm text-white/60 mb-2">信号拆分</div>
                    <div className="space-y-3">
                      {metrics.technicalPrediction.signalDetails.map((detail: any, index: number) => {
                        const signalColors = SIGNAL_COLOR_MAP[detail.signal] || SIGNAL_COLOR_MAP['NEUTRAL'];
                        return (
                          <div
                            key={`${detail.indicator}-${index}`}
                            className="bg-white/3 border border-white/10 rounded-lg p-3"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                              <div>
                                <div className="text-white font-semibold">{detail.indicator}</div>
                                <div className="text-xs text-white/50">权重 {(detail.weight * 100).toFixed(0)}%</div>
                              </div>
                              <div
                                className={`px-3 py-1 rounded-full text-sm font-semibold ${signalColors.bg} ${signalColors.text} ${signalColors.border}`}
                              >
                                {SIGNAL_LABEL_MAP[detail.signal] || '中性'}
                              </div>
                            </div>
                            <div className="mt-2 text-white/70 text-sm leading-relaxed">{detail.reasoning}</div>
                            <div className="mt-2 flex flex-wrap gap-3 text-xs text-white/50">
                              <span>信号值: {detail.signalValue.toFixed(3)}</span>
                              <span>贡献值: {detail.weightedValue.toFixed(3)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {metrics && (
            <div className="mt-6 space-y-4">
              {/* AI 联网搜索模块 */}
              <div className="bg-white/5 border border-white/20 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-bold text-white">AI 联网搜索</h2>
                  <button
                    onClick={handleAIWebSearch}
                    disabled={aiWebSearchLoading}
                    className="px-3 py-1.5 bg-blue-500/20 text-blue-200 border border-blue-500/40 rounded-lg hover:bg-blue-500/30 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {aiWebSearchLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>搜索中...</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                        <span>搜索消息</span>
                      </>
                    )}
                  </button>
                </div>

                {aiWebSearchResult && Object.keys(aiWebSearchResult).length > 0 ? (
                  <div className="space-y-3 text-sm text-white/80">
                    {(() => {
                      // 解析AI分析结果
                      let analysis = aiWebSearchResult.aiAnalysis;
                      if (typeof analysis === 'string') {
                        try {
                          const jsonMatch = analysis.match(/\{[\s\S]*\}/);
                          if (jsonMatch) {
                            analysis = JSON.parse(jsonMatch[0]);
                          }
                        } catch (error) {
                          console.error('解析 AI 搜索结果失败:', error);
                          analysis = { summary: analysis };
                        }
                      } else if (analysis?.content && typeof analysis.content === 'string') {
                        try {
                          const jsonMatch = analysis.content.match(/\{[\s\S]*\}/);
                          if (jsonMatch) {
                            analysis = JSON.parse(jsonMatch[0]);
                          }
                        } catch (error) {
                          console.error('解析 AI 搜索结果失败:', error);
                          analysis = { summary: analysis.content };
                        }
                      }

                      return (
                        <>
                          {/* 利好消息 */}
                          {analysis.bullishFactors &&
                            Array.isArray(analysis.bullishFactors) &&
                            analysis.bullishFactors.length > 0 && (
                              <div>
                                <div className="text-white font-semibold mb-1">利好消息</div>
                                <ul className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 space-y-2 text-green-200 list-disc list-inside">
                                  {analysis.bullishFactors.map((factor: any, index: number) => {
                                    // 处理对象格式：{factor, impact, description}
                                    if (typeof factor === 'object' && factor !== null) {
                                      const factorText = factor.factor || factor.description || JSON.stringify(factor);
                                      const impact = factor.impact ? ` (影响: ${factor.impact})` : '';
                                      return (
                                        <li key={index} className="leading-relaxed">
                                          {factorText}
                                          {impact}
                                        </li>
                                      );
                                    }
                                    // 处理字符串格式
                                    return <li key={index}>{factor}</li>;
                                  })}
                                </ul>
                              </div>
                            )}

                          {/* 利空消息 */}
                          {analysis.bearishFactors &&
                            Array.isArray(analysis.bearishFactors) &&
                            analysis.bearishFactors.length > 0 && (
                              <div>
                                <div className="text-white font-semibold mb-1">利空消息</div>
                                <ul className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 space-y-2 text-red-200 list-disc list-inside">
                                  {analysis.bearishFactors.map((factor: any, index: number) => {
                                    // 处理对象格式：{factor, impact, description}
                                    if (typeof factor === 'object' && factor !== null) {
                                      const factorText = factor.factor || factor.description || JSON.stringify(factor);
                                      const impact = factor.impact ? ` (影响: ${factor.impact})` : '';
                                      return (
                                        <li key={index} className="leading-relaxed">
                                          {factorText}
                                          {impact}
                                        </li>
                                      );
                                    }
                                    // 处理字符串格式
                                    return <li key={index}>{factor}</li>;
                                  })}
                                </ul>
                              </div>
                            )}

                          {/* 市场情绪 */}
                          {analysis.marketSentiment && (
                            <div>
                              <div className="text-white font-semibold mb-1">市场情绪</div>
                              {renderMarketSentimentBlock(analysis.marketSentiment)}
                            </div>
                          )}

                          {/* 风险评估 */}
                          {analysis.riskAssessment && (
                            <div>
                              <div className="text-white font-semibold mb-1">风险评估</div>
                              {renderRiskAssessmentBlock(analysis.riskAssessment)}
                            </div>
                          )}

                          {/* 操作建议 */}
                          {analysis.tradingAdvice && (
                            <div>
                              <div className="text-white font-semibold mb-1">操作建议</div>
                              {renderTradingAdviceBlock(analysis.tradingAdvice)}
                            </div>
                          )}

                          {/* 关键新闻 */}
                          {analysis.keyNews && Array.isArray(analysis.keyNews) && analysis.keyNews.length > 0 && (
                            <div>
                              <div className="text-white font-semibold mb-1">关键新闻</div>
                              <ul className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2 text-white/70">
                                {analysis.keyNews.map((news: string, index: number) => (
                                  <li key={index} className="leading-relaxed">
                                    {news}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-white/50 text-sm">
                    点击「搜索消息」按钮，获取该币种最新的利好、利空消息和市场分析。
                  </div>
                )}
              </div>

              {/* AI 技术面分析模块 */}
              <div className="bg-white/5 border border-white/20 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-bold text-white">AI 技术面分析</h2>
                  <button
                    onClick={handleAIAnalyze}
                    disabled={aiLoading}
                    className="px-3 py-1.5 bg-purple-500/20 text-purple-200 border border-purple-500/40 rounded-lg hover:bg-purple-500/30 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {aiLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>分析中...</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                        <span>AI 分析</span>
                      </>
                    )}
                  </button>
                </div>

                {aiAnalysis && Object.keys(aiAnalysis).length > 0 ? (
                  <div className="space-y-3 text-sm text-white/80">
                    {(() => {
                      // 解析AI分析结果：优先使用 analysis 字段，否则直接使用 aiAnalysis
                      let rawAnalysis = aiAnalysis.analysis || aiAnalysis;

                      // 如果 analysis 包含 content 字段（JSON 字符串），需要解析
                      let analysis: any = rawAnalysis;
                      if (rawAnalysis?.content && typeof rawAnalysis.content === 'string') {
                        try {
                          // 尝试解析 JSON 字符串
                          const jsonMatch = rawAnalysis.content.match(/\{[\s\S]*\}/);
                          if (jsonMatch) {
                            analysis = JSON.parse(jsonMatch[0]);
                          } else {
                            // 如果无法提取 JSON，使用原始 content
                            analysis = { summary: rawAnalysis.content };
                          }
                        } catch (error) {
                          console.error('解析 AI 分析结果失败:', error);
                          // 解析失败，尝试使用原始对象
                          analysis = rawAnalysis;
                        }
                      }

                      return (
                        <>
                          {/* 综合分析 */}
                          {analysis.summary && (
                            <div>
                              <div className="text-white font-semibold mb-1">综合分析</div>
                              <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-white/70 leading-relaxed">
                                {analysis.summary}
                              </div>
                            </div>
                          )}

                          {/* 关键信号 */}
                          {analysis.keySignals &&
                            Array.isArray(analysis.keySignals) &&
                            analysis.keySignals.length > 0 && (
                              <div>
                                <div className="text-white font-semibold mb-1">关键信号</div>
                                <ul className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-1 text-white/70 list-disc list-inside">
                                  {analysis.keySignals.map((signal: string, index: number) => (
                                    <li key={index}>{signal}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                          {/* 下跌概率预测 */}
                          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                            <div className="text-red-200 text-sm mb-1">下跌回调概率</div>
                            <div className="text-3xl font-bold text-red-100 mb-2">
                              {analysis.bearishProbability != null ? `${analysis.bearishProbability}%` : '-'}
                            </div>
                            <div className="text-xs text-red-300/70">
                              基于技术指标分析：动能衰竭、价格形态、价格偏离度、资金费率等信号
                            </div>
                          </div>

                          {/* 信心水平 */}
                          {analysis.confidenceLevel && (
                            <div>
                              <div className="text-white font-semibold mb-1">分析信心</div>
                              <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-white/70">
                                {analysis.confidenceLevel}
                              </div>
                            </div>
                          )}

                          {/* 未来走势 */}
                          {analysis.nextMoves && (
                            <div>
                              <div className="text-white font-semibold mb-1">未来走势</div>
                              <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-white/70 leading-relaxed">
                                {analysis.nextMoves}
                              </div>
                            </div>
                          )}

                          {/* 操作建议 */}
                          {analysis.recommendedStrategy && (
                            <div>
                              <div className="text-white font-semibold mb-1">操作建议</div>
                              <div className="bg-white/5 border border-white/10 rounded-lg p-3 pb-12 text-white/70 leading-relaxed relative">
                                {analysis.recommendedStrategy}
                                <button
                                  onClick={async () => {
                                    try {
                                      setRecommendedStrategy(analysis.recommendedStrategy); // 保存操作建议
                                      setOrderAnalysisLoading(true);
                                      setShowOrderDialog(true);
                                      const result = await coinMetricsService.analyzeTradingStrategy(
                                        playbackSymbol!,
                                        analysis.recommendedStrategy,
                                        selectedTimeframe,
                                      );
                                      setOrderAnalysis(result);
                                      // 根据返回的策略设置默认标签页
                                      if (result.longStrategy) {
                                        setActiveTab(POSITION_SIDE.LONG as PositionSide);
                                      } else if (result.shortStrategy) {
                                        setActiveTab(POSITION_SIDE.SHORT as PositionSide);
                                      }
                                    } catch (error: any) {
                                      showToast(error.message || 'AI下单分析失败', 'error');
                                      setShowOrderDialog(false);
                                      setRecommendedStrategy(null);
                                    } finally {
                                      setOrderAnalysisLoading(false);
                                    }
                                  }}
                                  className="absolute bottom-2 right-2 px-3 py-1.5 bg-purple-500/20 text-purple-200 border border-purple-500/40 rounded-lg hover:bg-purple-500/30 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                  disabled={orderAnalysisLoading}
                                >
                                  {orderAnalysisLoading ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-200"></div>
                                      <span>分析中...</span>
                                    </>
                                  ) : (
                                    <>
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M13 10V3L4 14h7v7l9-11h-7z"
                                        />
                                      </svg>
                                      <span>AI下单</span>
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}

                          {/* 风险因素 */}
                          {analysis.riskFactors &&
                            Array.isArray(analysis.riskFactors) &&
                            analysis.riskFactors.length > 0 && (
                              <div>
                                <div className="text-white font-semibold mb-1">风险因素</div>
                                <ul className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 space-y-1 text-yellow-200 list-disc list-inside">
                                  {analysis.riskFactors.map((risk: string, index: number) => (
                                    <li key={index}>{risk}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                          {/* 止损建议 */}
                          {analysis.stopLossSuggestion && (
                            <div>
                              <div className="text-white font-semibold mb-1">止损建议</div>
                              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-orange-200 leading-relaxed">
                                {analysis.stopLossSuggestion}
                              </div>
                            </div>
                          )}

                          {/* 完整结果（JSON） */}
                          <div>
                            <div className="text-white font-semibold mb-1">完整结果 (JSON)</div>
                            <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-white/60 text-xs whitespace-pre-wrap overflow-auto max-h-64">
                              {JSON.stringify(aiAnalysis.analysis || aiAnalysis, null, 2)}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-white/50 text-sm">
                    点击「AI 分析」按钮，获取针对当前币种技术指标的 AI 解读与概率预测。
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI下单对话框 */}
      {showOrderDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="relative bg-gradient-to-br from-purple-900/95 via-blue-900/95 to-indigo-900/95 border border-white/20 rounded-xl max-w-2xl w-full mx-4 min-h-[50vh] max-h-[90vh] flex flex-col shadow-2xl">
            {/* 背景光晕效果 */}
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-tr from-cyan-500/10 via-transparent to-purple-500/10 rounded-xl pointer-events-none" />
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-cyan-400/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-400/10 rounded-full blur-3xl pointer-events-none" />

            {/* 固定标题栏 */}
            <div className="relative z-10 flex items-center justify-between p-6 border-b border-white/10 flex-shrink-0">
              <h2 className="text-white text-xl font-semibold">AI下单策略</h2>
              <button
                onClick={() => {
                  setShowOrderDialog(false);
                  setOrderAnalysis(null);
                  setRecommendedStrategy(null);
                  setActiveTab(POSITION_SIDE.LONG as PositionSide);
                  setSubmittingOrder(false);
                  setLongOrderSubmitted(false);
                  setShortOrderSubmitted(false);
                }}
                className="text-white/70 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

            {/* 可滚动内容区域 */}
            <div className="relative z-10 flex-1 overflow-y-auto p-6 pt-4">
              {orderAnalysisLoading ? (
                <div className="space-y-4">
                  <div className="text-white/70 text-center py-4">
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                      <span>分析中...</span>
                    </div>
                  </div>
                  {recommendedStrategy && (
                    <div>
                      <div className="text-white font-semibold mb-2">操作建议</div>
                      <div className="bg-white/5 border border-white/10 rounded-lg p-4 text-white/70 leading-relaxed">
                        {recommendedStrategy}
                      </div>
                    </div>
                  )}
                </div>
              ) : orderAnalysis ? (
                <div className="space-y-4">
                  {/* 标签页切换 */}
                  {(orderAnalysis.longStrategy || orderAnalysis.shortStrategy) && (
                    <div className="flex gap-2 border-b border-white/10">
                      {orderAnalysis.longStrategy && (
                        <button
                          onClick={() => setActiveTab(POSITION_SIDE.LONG as PositionSide)}
                          className={`px-4 py-2 font-semibold transition-colors ${
                            activeTab === POSITION_SIDE.LONG
                              ? 'text-green-400 border-b-2 border-green-400'
                              : 'text-white/60 hover:text-white/80'
                          }`}
                        >
                          做多策略
                        </button>
                      )}
                      {orderAnalysis.shortStrategy && (
                        <button
                          onClick={() => setActiveTab(POSITION_SIDE.SHORT as PositionSide)}
                          className={`px-4 py-2 font-semibold transition-colors ${
                            activeTab === POSITION_SIDE.SHORT
                              ? 'text-red-400 border-b-2 border-red-400'
                              : 'text-white/60 hover:text-white/80'
                          }`}
                        >
                          做空策略
                        </button>
                      )}
                    </div>
                  )}

                  {/* 做多策略 */}
                  {orderAnalysis.longStrategy && activeTab === POSITION_SIDE.LONG && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 backdrop-blur-sm">
                      <h3 className="text-green-400 font-semibold mb-3">做多策略</h3>
                      <div className="space-y-2 text-sm text-white/80">
                        {orderAnalysis.longStrategy.entryPrice && (
                          <div>
                            入场价格: <span className="text-white">{orderAnalysis.longStrategy.entryPrice}</span>
                          </div>
                        )}
                        {orderAnalysis.longStrategy.targetPrice && (
                          <div>
                            止盈价格: <span className="text-green-400">{orderAnalysis.longStrategy.targetPrice}</span>
                          </div>
                        )}
                        {orderAnalysis.longStrategy.stopLossPrice && (
                          <div>
                            止损价格: <span className="text-red-400">{orderAnalysis.longStrategy.stopLossPrice}</span>
                          </div>
                        )}
                        <div>
                          风险等级:{' '}
                          <span className="text-white">
                            {orderAnalysis.longStrategy.riskLevel === 'low'
                              ? '低风险'
                              : orderAnalysis.longStrategy.riskLevel === 'medium'
                                ? '中风险'
                                : '高风险'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span>仓位大小:</span>
                          <input
                            type="number"
                            value={positionSize}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              if (!isNaN(value) && value > 0) {
                                setPositionSize(value);
                              }
                            }}
                            min="1"
                            step="1"
                            className="px-2 py-1 bg-white/10 border border-white/20 rounded text-white w-24 focus:outline-none focus:border-green-400"
                            placeholder="2000"
                          />
                          <span className="text-white">USDT</span>
                        </div>
                        {orderAnalysis.longStrategy.indicators && orderAnalysis.longStrategy.indicators.length > 0 && (
                          <div className="mt-2">
                            <div className="font-semibold mb-1">监控指标:</div>
                            <ul className="list-disc list-inside space-y-1">
                              {orderAnalysis.longStrategy.indicators.map((ind: any, idx: number) => (
                                <li key={idx}>
                                  {ind.name}: {ind.value} - {ind.description}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {orderAnalysis.longStrategy.conditions && orderAnalysis.longStrategy.conditions.length > 0 && (
                          <div className="mt-2">
                            <div className="font-semibold mb-1">触发条件:</div>
                            <ul className="list-disc list-inside space-y-1">
                              {orderAnalysis.longStrategy.conditions.map((cond: any, idx: number) => (
                                <li key={idx}>{cond.description || `${cond.field} ${cond.operator} ${cond.value}`}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      <div className="mt-4 flex gap-2">
                        <button
                          onClick={async () => {
                            if (submittingOrder) return; // 防止重复点击
                            try {
                              setSubmittingOrder(true);
                              await coinMetricsService.submitOrderTask(
                                playbackSymbol!,
                                POSITION_SIDE.LONG,
                                orderAnalysis.longStrategy.entryPrice,
                                orderAnalysis.longStrategy.targetPrice,
                                orderAnalysis.longStrategy.stopLossPrice,
                                orderAnalysis.longStrategy.indicators,
                                orderAnalysis.longStrategy.conditions,
                                orderAnalysis.longStrategy.riskLevel,
                                positionSize,
                                selectedTimeframe,
                              );
                              showToast('做多订单任务已加入队列', 'success');
                              setLongOrderSubmitted(true);
                              setActiveTab(POSITION_SIDE.LONG as PositionSide);
                            } catch (error: any) {
                              showToast(error.message || '提交订单失败', 'error');
                            } finally {
                              setSubmittingOrder(false);
                            }
                          }}
                          disabled={submittingOrder || longOrderSubmitted}
                          className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {submittingOrder ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>提交中...</span>
                            </>
                          ) : longOrderSubmitted ? (
                            '已下单'
                          ) : (
                            '执行策略'
                          )}
                        </button>
                        <button
                          onClick={async () => {
                            if (submittingOrder || longOrderSubmitted) return; // 防止重复点击
                            try {
                              setSubmittingOrder(true);
                              await coinMetricsService.executeOrderImmediately(
                                playbackSymbol!,
                                POSITION_SIDE.LONG,
                                orderAnalysis.longStrategy.entryPrice,
                                orderAnalysis.longStrategy.targetPrice,
                                orderAnalysis.longStrategy.stopLossPrice,
                                orderAnalysis.longStrategy.indicators,
                                orderAnalysis.longStrategy.conditions,
                                orderAnalysis.longStrategy.riskLevel,
                                positionSize,
                              );
                              showToast('做多订单已立即执行', 'success');
                              setLongOrderSubmitted(true);
                              setActiveTab(POSITION_SIDE.LONG as PositionSide);
                            } catch (error: any) {
                              showToast(error.message || '立即下单失败', 'error');
                            } finally {
                              setSubmittingOrder(false);
                            }
                          }}
                          disabled={submittingOrder || longOrderSubmitted}
                          className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {submittingOrder ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>下单中...</span>
                            </>
                          ) : longOrderSubmitted ? (
                            '已下单'
                          ) : (
                            '立即下单'
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 做空策略 */}
                  {orderAnalysis.shortStrategy && activeTab === POSITION_SIDE.SHORT && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 backdrop-blur-sm">
                      <h3 className="text-red-400 font-semibold mb-3">做空策略</h3>
                      <div className="space-y-2 text-sm text-white/80">
                        {orderAnalysis.shortStrategy.entryPrice && (
                          <div>
                            入场价格: <span className="text-white">{orderAnalysis.shortStrategy.entryPrice}</span>
                          </div>
                        )}
                        {orderAnalysis.shortStrategy.targetPrice && (
                          <div>
                            止盈价格: <span className="text-green-400">{orderAnalysis.shortStrategy.targetPrice}</span>
                          </div>
                        )}
                        {orderAnalysis.shortStrategy.stopLossPrice && (
                          <div>
                            止损价格: <span className="text-red-400">{orderAnalysis.shortStrategy.stopLossPrice}</span>
                          </div>
                        )}
                        <div>
                          风险等级:{' '}
                          <span className="text-white">
                            {orderAnalysis.shortStrategy.riskLevel === 'low'
                              ? '低风险'
                              : orderAnalysis.shortStrategy.riskLevel === 'medium'
                                ? '中风险'
                                : '高风险'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span>仓位大小:</span>
                          <input
                            type="number"
                            value={positionSize}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              if (!isNaN(value) && value > 0) {
                                setPositionSize(value);
                              }
                            }}
                            min="1"
                            step="1"
                            className="px-2 py-1 bg-white/10 border border-white/20 rounded text-white w-24 focus:outline-none focus:border-red-400"
                            placeholder="2000"
                          />
                          <span className="text-white">USDT</span>
                        </div>
                        {orderAnalysis.shortStrategy.indicators &&
                          orderAnalysis.shortStrategy.indicators.length > 0 && (
                            <div className="mt-2">
                              <div className="font-semibold mb-1">监控指标:</div>
                              <ul className="list-disc list-inside space-y-1">
                                {orderAnalysis.shortStrategy.indicators.map((ind: any, idx: number) => (
                                  <li key={idx}>
                                    {ind.name}: {ind.value} - {ind.description}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        {orderAnalysis.shortStrategy.conditions &&
                          orderAnalysis.shortStrategy.conditions.length > 0 && (
                            <div className="mt-2">
                              <div className="font-semibold mb-1">触发条件:</div>
                              <ul className="list-disc list-inside space-y-1">
                                {orderAnalysis.shortStrategy.conditions.map((cond: any, idx: number) => (
                                  <li key={idx}>
                                    {cond.description || `${cond.field} ${cond.operator} ${cond.value}`}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                      </div>
                      <div className="mt-4 flex gap-2">
                        <button
                          onClick={async () => {
                            if (submittingOrder) return; // 防止重复点击
                            try {
                              setSubmittingOrder(true);
                              await coinMetricsService.submitOrderTask(
                                playbackSymbol!,
                                POSITION_SIDE.SHORT,
                                orderAnalysis.shortStrategy.entryPrice,
                                orderAnalysis.shortStrategy.targetPrice,
                                orderAnalysis.shortStrategy.stopLossPrice,
                                orderAnalysis.shortStrategy.indicators,
                                orderAnalysis.shortStrategy.conditions,
                                orderAnalysis.shortStrategy.riskLevel,
                                positionSize,
                                selectedTimeframe,
                              );
                              showToast('做空订单任务已加入队列', 'success');
                              setShortOrderSubmitted(true);
                              setActiveTab(POSITION_SIDE.LONG as PositionSide);
                            } catch (error: any) {
                              showToast(error.message || '提交订单失败', 'error');
                            } finally {
                              setSubmittingOrder(false);
                            }
                          }}
                          disabled={submittingOrder || shortOrderSubmitted}
                          className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {submittingOrder ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>提交中...</span>
                            </>
                          ) : shortOrderSubmitted ? (
                            '已下单'
                          ) : (
                            '执行策略'
                          )}
                        </button>
                        <button
                          onClick={async () => {
                            if (submittingOrder || shortOrderSubmitted) return; // 防止重复点击
                            try {
                              setSubmittingOrder(true);
                              await coinMetricsService.executeOrderImmediately(
                                playbackSymbol!,
                                POSITION_SIDE.SHORT,
                                orderAnalysis.shortStrategy.entryPrice,
                                orderAnalysis.shortStrategy.targetPrice,
                                orderAnalysis.shortStrategy.stopLossPrice,
                                orderAnalysis.shortStrategy.indicators,
                                orderAnalysis.shortStrategy.conditions,
                                orderAnalysis.shortStrategy.riskLevel,
                                positionSize,
                              );
                              showToast('做空订单已立即执行', 'success');
                              setShortOrderSubmitted(true);
                              setActiveTab(POSITION_SIDE.LONG as PositionSide);
                            } catch (error: any) {
                              showToast(error.message || '立即下单失败', 'error');
                            } finally {
                              setSubmittingOrder(false);
                            }
                          }}
                          disabled={submittingOrder || shortOrderSubmitted}
                          className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {submittingOrder ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>下单中...</span>
                            </>
                          ) : shortOrderSubmitted ? (
                            '已下单'
                          ) : (
                            '立即下单'
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {!orderAnalysis.longStrategy && !orderAnalysis.shortStrategy && (
                    <div className="text-white/70 text-center py-4">未生成交易策略，请检查操作建议内容</div>
                  )}
                </div>
              ) : (
                <div className="text-white/70 text-center py-8">暂无数据</div>
              )}
            </div>
          </div>
        </div>
      )}
    </GradientBackground>
  );
};

export default CoinMetricsAnalysis;
